import { MenuItem, TextField } from "@mui/material";
import { getCfdiUseOptions, formatCfdiUse } from "hvp-shared";

interface Props {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Receiver fiscal regime — decides which keys the SAT admits. */
  fiscalRegime?: string;
  /** Receiver RFC. 12 positions is a persona moral, 13 a persona física. */
  rfc?: string;
  label?: string;
  /** Extra line under the field, shown before the "filtered list" note. */
  hint?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  margin?: "none" | "dense" | "normal";
}

/**
 * "Uso del CFDI" picker, filtered by the receiver's fiscal regime.
 *
 * The SAT only admits certain keys per regime and the PAC rejects the stamp
 * otherwise, so offering the whole catalog just moves the error later. The field
 * always says the list is filtered: an absent key has to read as a rule and not
 * as a bug.
 *
 * A stored value the regime no longer admits (an old P01, or a regime that was
 * edited afterwards) is kept in the list and flagged, never dropped silently.
 */
export const CfdiUseSelect = ({
  value,
  onChange,
  onBlur,
  fiscalRegime,
  rfc,
  label = "Uso del CFDI",
  hint,
  error,
  errorMessage,
  disabled,
  margin = "dense",
}: Props) => {
  const regime = fiscalRegime?.trim() ?? "";
  const options = regime ? getCfdiUseOptions(regime, rfc) : [];
  const isStale = !!value && !options.some((o) => o.code === value);

  const helperText = errorMessage
    ? errorMessage
    : !regime
      ? "Elige primero el régimen fiscal: de él depende qué usos admite el SAT."
      : isStale
        ? `${formatCfdiUse(value)} no está permitido con el régimen ${regime}. Elige otro de la lista.`
        : [hint, `Solo se muestran los usos que el SAT admite para el régimen ${regime}.`]
            .filter(Boolean)
            .join(" ");

  return (
    <TextField
      select
      fullWidth
      margin={margin}
      label={label}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      disabled={disabled || !regime}
      error={error || isStale}
      helperText={helperText}
    >
      {isStale && (
        <MenuItem value={value} disabled>
          {formatCfdiUse(value)} — no válido para este régimen
        </MenuItem>
      )}
      {options.map((o) => (
        <MenuItem key={o.code} value={o.code}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
};
